import { Search } from "@/types/Search";
import { TableRow, TableCell } from "@components/ui/table";
import { ChevronDownIcon, MailCheckIcon, MailXIcon } from "lucide-react";
import { useCappedList } from "@/hooks/useCappedList";
import { Trans } from "@lingui/react/macro";
import { useFormatSubscribers } from "@/hooks/useFormatSubscribers";
import { i18n } from "@lingui/core";
import {
  SEARCH_SUBSCRIPTION_INTERVAL,
  SEARCH_SUBSCRIPTION_INTERVAL_TO_LABEL,
  SearchSubscriptionInterval,
} from "@/constants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuGroup,
  DropdownMenuLabel,
} from "@components/ui/dropdown-menu";
import { Button } from "@components/ui/button";
import { SearchSubscription } from "@/types/SearchSubscription";
import { cn } from "@/lib/utils";
import { User } from "@/types/User";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";
import { generatePath, Link } from "react-router-dom";
import { ROUTES } from "@/routes_new";
import { Organization } from "@/types/Organization";

type SearchRowProps = {
  onCreateSearchSubscription: (interval: SearchSubscriptionInterval) => void;
  onDeleteSearchSubscription: (id: SearchSubscription["id"]) => void;
  availableSearchSubscriptions: SearchSubscriptionInterval[];
  allowedIntervals?: SearchSubscriptionInterval[];
  searchSubscriptions: SearchSubscription[];
  isCreatingSearchSubscription: boolean;
  isDeletingSearchSubscription: boolean;
  isShowingSubscribers?: boolean;
  orgUuid: Organization["uuid"];
  userId: User["id"];
  search: Search;
};

export default function SearchRow({
  isCreatingSearchSubscription,
  isDeletingSearchSubscription,
  availableSearchSubscriptions,
  allowedIntervals,
  isShowingSubscribers = true,
  onCreateSearchSubscription,
  onDeleteSearchSubscription,
  searchSubscriptions,
  orgUuid,
  userId,
  search,
}: SearchRowProps) {
  const formatCappedList = useCappedList();
  const formatSubscribers = useFormatSubscribers();

  // Helpers
  const availableDirectSearchSubscriptions =
    availableSearchSubscriptions.filter(
      (a) => a !== SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY
    );
  const availableSummarySearchSubscriptions =
    availableSearchSubscriptions.filter(
      (a) => a === SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY
    );

  const { isVisible: isLoading } = useDelayedLoading(
    isCreatingSearchSubscription || isDeletingSearchSubscription,
    300
  );

  const renderOptions = (options: SearchSubscriptionInterval[]) =>
    options.toSorted().map((option) => {
      const searchSubscription = searchSubscriptions.find(
        (ss) => ss.interval === option
      );
      const isEnabled = !allowedIntervals || allowedIntervals.includes(option);

      return (
        <DropdownMenuCheckboxItem
          key={option}
          checked={!!searchSubscription}
          disabled={!isEnabled}
          isLoading={isLoading}
          onCheckedChange={(newChecked) => {
            if (newChecked) {
              onCreateSearchSubscription(option);
            } else if (searchSubscription) {
              onDeleteSearchSubscription(searchSubscription.id);
            }
          }}
          onSelect={(e) => {
            e.preventDefault();
          }}
        >
          {i18n._(SEARCH_SUBSCRIPTION_INTERVAL_TO_LABEL[option])}
        </DropdownMenuCheckboxItem>
      );
    });

  return (
    <TableRow>
      <TableCell className="font-medium truncate">
        <Link
          className="link"
          to={generatePath(ROUTES.SAVED_SEARCHES_DETAIL_ORGID, {
            orgUuid,
            uuid: search.uuid,
          })}
        >
          {search.title}
        </Link>
      </TableCell>

      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "text-left justify-between font-normal max-w-full",
                !searchSubscriptions.length && "text-muted-foreground"
              )}
            >
              {searchSubscriptions.length ? (
                <>
                  <MailCheckIcon />

                  <span className="truncate mr-auto">
                    {formatCappedList(
                      searchSubscriptions
                        .toSorted((a, b) =>
                          a.interval.localeCompare(b.interval)
                        )
                        .map((as) =>
                          i18n._(
                            SEARCH_SUBSCRIPTION_INTERVAL_TO_LABEL[as.interval]
                          )
                        )
                    )}
                  </span>
                </>
              ) : (
                <>
                  <MailXIcon />

                  <Trans>Prenumererar inte</Trans>
                </>
              )}
              <ChevronDownIcon className="text-muted-foreground opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)]">
            <DropdownMenuGroup>
              <DropdownMenuLabel>
                <Trans>Mejla vid nya omnämnanden</Trans>
              </DropdownMenuLabel>
              {renderOptions(availableDirectSearchSubscriptions)}
            </DropdownMenuGroup>

            {availableSummarySearchSubscriptions.length ? (
              <DropdownMenuGroup>
                <DropdownMenuLabel>
                  <Trans>Mejla en samanfatnning</Trans>
                </DropdownMenuLabel>

                {renderOptions(availableSummarySearchSubscriptions)}
              </DropdownMenuGroup>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>

      {isShowingSubscribers && (
        <TableCell>{formatSubscribers(search.subscriptions, userId)}</TableCell>
      )}
    </TableRow>
  );
}
